import React from 'react';
import { Box, Typography, Button, Paper, useTheme } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * ErrorState - Displayed when an API call fails
 * Provides a friendly error message and retry option.
 *
 * @param {Object} props
 * @param {string} props.message - Error message to display
 * @param {Function} props.onRetry - Callback when user clicks retry
 * @param {string} props.themeColor - Theme color for styling (default: the theme's primary colour)
 */
export default function ErrorState({
  message = 'Something went wrong while loading the data.',
  onRetry,
  themeColor = undefined
}) {
  const theme = useTheme();
  const color = themeColor || theme.palette.primary.main;
  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        textAlign: 'center',
        borderRadius: 3,
        border: '1px solid #ffcdd2',
        bgcolor: '#ffebee'
      }}
    >
      <ErrorOutlineIcon sx={{ fontSize: 64, color: '#d32f2f', mb: 2 }} />

      <Typography variant="h6" gutterBottom sx={{ color: '#c62828' }}>
        Oops! Something went wrong
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
        {message}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          This could be due to:
        </Typography>
        <ul style={{ display: 'inline-block', textAlign: 'left', margin: '8px 0' }}>
          <li>
            <Typography variant="body2" color="text.secondary">
              A temporary network issue
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="text.secondary">
              The search service being temporarily unavailable
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="text.secondary">
              An invalid search query
            </Typography>
          </li>
        </ul>
      </Box>

      {onRetry && (
        <Button
          variant="contained"
          onClick={onRetry}
          startIcon={<RefreshIcon />}
          sx={{
            borderRadius: 2,
            bgcolor: color,
            '&:hover': {
              bgcolor: color,
              filter: 'brightness(0.9)'
            }
          }}
        >
          Try Again
        </Button>
      )}

      <Box sx={{ mt: 3 }}>
        <Typography variant="caption" color="text.secondary">
          If the problem persists, please try refreshing the page or contact support.
        </Typography>
      </Box>
    </Paper>
  );
}
